// Persist the last few emails the Captain has signed in with so the
// SignIn page can offer a dropdown + browser autocomplete on the
// next visit.
//
// Storage: `wt_recent_emails` cookie, JSON-encoded array of strings,
// most-recent-first, capped at MAX. Cookie was the Captain's
// suggested mechanism — it survives most "clear browser data"
// flavours that wipe localStorage (cookie-only is a separate
// checkbox in Chrome/Firefox) and the small list (≤5 short
// strings) sits well under the 4 KB per-cookie limit.
//
// Cookie attributes:
//   - max-age=31536000 (1 year)
//   - path=/ so any future SignIn-adjacent route reads it
//   - SameSite=Lax so it travels on the same-site OAuth-callback
//     redirect (GitHub) but doesn't leak on cross-site requests
//   - no Secure flag on localhost; on staging / production the
//     site is HTTPS-only so the browser keeps it secure regardless
//   - no HttpOnly because the cookie is ONLY read by client JS
//     (the backend has no use for it)

const COOKIE_NAME = "wt_recent_emails";
const MAX = 5;
const ONE_YEAR_SECS = 60 * 60 * 24 * 365;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) return decodeURIComponent(trimmed.slice(target.length));
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  document.cookie =
    `${name}=${encodeURIComponent(value)}; ` +
    `max-age=${ONE_YEAR_SECS}; ` +
    `path=/; ` +
    `SameSite=Lax`;
}

export function getRecentEmails(): string[] {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === "string").slice(0, MAX);
  } catch {
    return [];
  }
}

/** Push `email` to the front of the recents list, dedupe, cap at MAX,
 * write back. Idempotent — repeated calls with the same email just
 * keep it at the front. Caller is responsible for trim/lowercase. */
export function rememberEmail(email: string): void {
  const normalised = email.trim().toLowerCase();
  if (!normalised || !normalised.includes("@")) return;
  const existing = getRecentEmails();
  const next = [normalised, ...existing.filter((e) => e !== normalised)].slice(0, MAX);
  writeCookie(COOKIE_NAME, JSON.stringify(next));
}

/** Remove a specific email — currently unused, but useful when we
 * surface a "remove this suggestion" affordance on the dropdown. */
export function forgetEmail(email: string): void {
  const normalised = email.trim().toLowerCase();
  const next = getRecentEmails().filter((e) => e !== normalised);
  writeCookie(COOKIE_NAME, JSON.stringify(next));
}
